import React from "react";
import { Avatar } from "../../../../../ui/components/Avatar";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { TeamMemberDTO } from "../../data/api/teamMembersApi";

/**
 * Get initials from a name
 */
const getInitials = (name: string): string => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Format role for display
 */
const formatRole = (role: string): string => {
  // Convert snake_case to Title Case
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const YourJournalTeam: React.FC = () => {
  const { teamMembers, loading, error } = useTeamMembers();

  if (loading) {
    return (
      <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Your Zournal team
            </span>
            <span className="text-body font-body text-subtext-color">
              Have questions? Reach out to your dedicated team at Zournal.
            </span>
          </div>
        </div>
        <div className="text-body text-subtext-color">
          Loading team members...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Your Zournal team
            </span>
            <span className="text-body font-body text-subtext-color">
              Have questions? Reach out to your dedicated team at Zournal.
            </span>
          </div>
        </div>
        <div className="text-body text-error-600">
          Error loading team members: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Your Zournal team
          </span>
          <span className="text-body font-body text-subtext-color">
            Have questions? Reach out to your dedicated team at Zournal.
          </span>
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        {teamMembers.length === 0 ? (
          <div className="text-body text-subtext-color">
            No team members found.
          </div>
        ) : (
          teamMembers.map((member: TeamMemberDTO) => {
            const initials = getInitials(member.userName || member.email || "");
            const displayName =
              member.userName || member.email || `User ${member.id}`;
            const displayRole = formatRole(member.role);

            return (
              <div
                key={member.id}
                className="flex w-full items-center gap-4 py-2"
              >
                <Avatar
                  size="large"
                  image={member.profile_picture || undefined}
                  variant="neutral"
                >
                  {!member.profile_picture ? initials : undefined}
                </Avatar>
                <div className="flex flex-col items-start gap-1 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {displayName}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-body font-body text-subtext-color">
                      {displayRole}
                    </span>
                    {member.phone_number && (
                      <span className="text-body font-body text-subtext-color">
                        • {member.phone_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default YourJournalTeam;
